import { HStack, Icon, Link, Stack, Text, useDisclosure } from '@chakra-ui/react';
import { PencilIcon } from '@heroicons/react/solid';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import {
  ISubscription,
  IdentityVerificationStatus,
  getBrand,
  getHostName,
  isFeatureEnabled,
  routes,
} from '@blockpulse3/data/shared';
import { CompanyStatus, IdentityType } from '@blockpulse3/graphql/hooks';
import {
  IdentityAvatar,
  IdentityCard,
  IdentityCardDescription,
  IdentityCardTitle,
  PhoneBanner,
} from '@blockpulse3/ui/commons';
import { useUser } from '@blockpulse3/web-client/auth';
import { Wallet } from '@blockpulse3/web-client/shareholding';

import { GenderPicker } from './GenderPicker';
import { IdentityHeader } from './IdentityHeader';
import { InvitationList } from './InvitationList';
import { CompanyVerificationModal, UserVerificationModal } from './VerificationModal';

type Props = unknown;

/**
 * UserDashboardView.
 * Dashboard of the authentified user.
 *
 * @returns {JSX.Element}
 */
export function UserDashboardView(): JSX.Element {
  const i18nIdentityHeader = useTranslations('IdentityHeader');

  const navigate = useNavigate();
  const { user, loadUser } = useUser();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const [subCompanyId, setSubCompanyId] = useState<string | undefined>(undefined);
  const [subscriptionId, setSubscriptionId] = useState<string>('');

  const renderPhoneBanner = isFeatureEnabled(getBrand(getHostName()), 'phoneBanner');

  const userModal = useDisclosure();
  const companyModal = useDisclosure();

  const handleInvitationClick = (subscription: ISubscription, subCompanyId?: string): void => {
    setSubCompanyId(subCompanyId);

    if (subCompanyId) {
      const company = subscription.identity.companyIdentity;
      if (company?.status === CompanyStatus.RUNNING) {
        if (subscription.identity.verificationStatus === IdentityVerificationStatus.APPROVED) {
          navigate(routes.workspace.subscription.fmt(subCompanyId, subscription.id));
        } else {
          setSubscriptionId(subscription.id);
          companyModal.onOpen();
        }
      } else {
        navigate(routes.onboardings.company.infos.fmt(subCompanyId));
      }
    } else {
      if (subscription.identity.verificationStatus === IdentityVerificationStatus.APPROVED) {
        navigate(routes.me.subscription.fmt(subscription.id));
      } else {
        setSubscriptionId(subscription.id);
        userModal.onOpen();
      }
    }
  };

  const handleFormSubmit = (): void => {
    loadUser();
  };

  const handleVerificationModalClose = (): void => {
    setSubscriptionId('');
    userModal.onClose();
    companyModal.onClose();
  };

  const handleVerificationModify = (): void => {
    if (subCompanyId) {
      navigate(routes.workspace.settings.fmt(subCompanyId));
    } else {
      navigate(routes.me.settings.href);
    }
  };

  const identity = user?.individualIdentity;
  const profilePicture = user?.individualIdentity?.identity?.profilePicture || '';
  const isVerified = identity?.kycVerificationStatus === IdentityVerificationStatus.APPROVED;

  return (
    <Stack spacing="6">
      {!user?.individualIdentity?.gender && (
        <GenderPicker defaultValues={{ gender: '' }} user={user} onSubmit={handleFormSubmit} />
      )}
      <IdentityHeader>
        <IdentityCard onMouseEnter={onOpen} onMouseLeave={onClose}>
          <IdentityAvatar boxSize="14" src={profilePicture} type={IdentityType.INDIVIDUAL} />
          <Stack spacing="0">
            <HStack spacing="3">
              <IdentityCardTitle isChecked={isVerified}>
                <Text>{identity?.name}</Text>
              </IdentityCardTitle>
              {isOpen && (
                <HStack fontSize="sm" spacing="1">
                  <Icon as={PencilIcon} />
                  <Link fontWeight="400" href={routes.me.settings.href}>
                    {i18nIdentityHeader('EditInfos')}
                  </Link>
                </HStack>
              )}
              <Link
                display={{ base: 'block', md: 'none' }}
                fontWeight="400"
                href={routes.me.settings.href}
              >
                <Icon as={PencilIcon} />
              </Link>
            </HStack>
            <IdentityCardDescription>
              <Text>{identity?.address?.formatted}</Text>
              <Text>{identity?.email}</Text>
              <Text>{identity?.phone}</Text>
            </IdentityCardDescription>
          </Stack>
        </IdentityCard>
      </IdentityHeader>
      {renderPhoneBanner && <PhoneBanner />}
      <Wallet />
      <InvitationList onInvitationClick={handleInvitationClick} />
      <UserVerificationModal
        isOpen={userModal.isOpen}
        subscriptionId={subscriptionId}
        onCancel={handleVerificationModalClose}
        onClose={handleVerificationModalClose}
        onModify={handleVerificationModify}
      />
      <CompanyVerificationModal
        isOpen={companyModal.isOpen}
        subCompanyId={subCompanyId}
        subscriptionId={subscriptionId}
        onCancel={handleVerificationModalClose}
        onClose={handleVerificationModalClose}
        onModify={handleVerificationModify}
      />
    </Stack>
  );
}

export type UserDashboardViewProps = Props;
